import type { AccessCode } from '@gat-ship/types'
import { accessCodeMetadata } from '../access-control-text'
import * as permissions from '../model/access-role-permissions'
import * as group from '../model/access-role-permission-group'
import type { PermissionGroupItem } from '../model/access-role-permission-group'

/**
 * View-model seam over the permission functional core. Components must reach the
 * pure deciders in `model/` through a composable (the MVVM rule), so this hook
 * injects the `accessCodeMetadata` table and delegates each decision to the
 * core. It holds no reactive state of its own.
 */
export function usePermissionDecisions() {
  const titleOf = (code: AccessCode) => accessCodeMetadata[code].title

  // ── Per-item decisions ──────────────────────────────────────────────
  function isGranted(
    accessCode: AccessCode,
    permissionCodes: readonly string[]
  ) {
    return permissions.isPermissionGranted(
      accessCodeMetadata[accessCode],
      permissionCodes
    )
  }

  function isDisabled(
    accessCode: AccessCode,
    permissionCodes: readonly string[]
  ) {
    return permissions.isPermissionDisabled(
      accessCodeMetadata[accessCode],
      permissionCodes
    )
  }

  function isHidden(
    accessCode: AccessCode,
    permissionCodes: readonly string[],
    hideRequiredPermissions: boolean
  ) {
    return permissions.isPermissionHidden(
      accessCodeMetadata[accessCode],
      permissionCodes,
      hideRequiredPermissions
    )
  }

  function matchesSearch(
    accessCode: AccessCode,
    context: { title: string; groupName: string },
    searchValue: string
  ) {
    return permissions.permissionMatchesSearch(
      accessCodeMetadata[accessCode],
      context,
      searchValue
    )
  }

  function itemIsVisible(
    accessCode: AccessCode,
    permissionCodes: readonly string[],
    options: {
      searchValue: string | undefined
      hideRequiredPermissions: boolean
      title: string
      groupName: string
    }
  ) {
    return permissions.permissionItemIsVisible(
      accessCodeMetadata[accessCode],
      permissionCodes,
      options
    )
  }

  function rolesRequiredText(
    accessCode: AccessCode,
    permissionCodes: readonly string[]
  ) {
    return permissions.rolesRequiredText(
      accessCodeMetadata[accessCode],
      permissionCodes,
      titleOf
    )
  }

  // ── Group-level decisions ───────────────────────────────────────────
  function visibleGroupItems(
    items: readonly PermissionGroupItem[],
    permissionCodes: readonly string[]
  ) {
    return group.visibleGroupPermissionItems(items, permissionCodes)
  }

  function grantedGroupItems(
    items: readonly PermissionGroupItem[],
    permissionCodes: readonly string[]
  ) {
    return group.grantedGroupPermissionItems(items, permissionCodes)
  }

  function hiddenGroupItems(
    items: readonly PermissionGroupItem[],
    permissionCodes: readonly string[],
    hideRequiredPermissions: boolean
  ) {
    return group.hiddenGroupPermissionItems(
      items,
      permissionCodes,
      hideRequiredPermissions
    )
  }

  function allGroupGranted(
    items: readonly PermissionGroupItem[],
    permissionCodes: readonly string[]
  ) {
    return group.allGroupPermissionsGranted(items, permissionCodes)
  }

  function allGroupRevoked(
    items: readonly PermissionGroupItem[],
    permissionCodes: readonly string[]
  ) {
    return group.allGroupPermissionsRevoked(items, permissionCodes)
  }

  return {
    isGranted,
    isDisabled,
    isHidden,
    matchesSearch,
    itemIsVisible,
    rolesRequiredText,
    visibleGroupItems,
    grantedGroupItems,
    hiddenGroupItems,
    allGroupGranted,
    allGroupRevoked
  }
}
